import _ from 'lodash';
import type { z } from 'zod';
import { SearchStringSchema } from '../common/schemas';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId } from '../recordIds';
import { LegacyTopicAttributes } from './constants';
import { DBLegacyTopicSchema, LegacyTopicSchema } from './schemas';

type LegacyTopic = z.infer<typeof LegacyTopicSchema>;

export const LegacyTopicCodecConfig: TransformerConfig<
  typeof LegacyTopicSchema,
  typeof DBLegacyTopicSchema,
  'id' | 'topicGroupId'
> = {
  name: 'LegacyTopic',
  clientSchema: LegacyTopicSchema,
  dbSchema: DBLegacyTopicSchema,
  transformers: [
    createRenameTransformer<typeof LegacyTopicSchema, typeof DBLegacyTopicSchema>(
      'code',
      'topicId'
    ),
    createRenameTransformer<typeof LegacyTopicSchema, typeof DBLegacyTopicSchema>(
      'topicGroupCode',
      'topicGroup'
    ),

    // examCodes: [examCode] ↔ exams: { examCode: {} }
    createTransformer<typeof LegacyTopicSchema, typeof DBLegacyTopicSchema>({
      encoder: {
        sourceFields: ['examCodes'] as const,
        encode: ({ examCodes }) => ({
          exams: Object.fromEntries(
            (examCodes as LegacyTopic['examCodes']).map(examCode => [examCode, {}])
          ),
        }),
      },
      decoder: {
        sourceFields: ['exams'] as const,
        decode: ({ exams }) => ({
          examCodes: Object.keys(exams ?? {}),
        }),
      },
    }),

    // Handle search string updates
    createTransformer<typeof LegacyTopicSchema, typeof DBLegacyTopicSchema>({
      encoder: {
        sourceFields: LegacyTopicAttributes.Search,
        dependencies: LegacyTopicAttributes.Search,
        encode: topic => {
          let searchStr = '';
          LegacyTopicAttributes.Search.forEach(attr => {
            const val = topic[attr];
            searchStr += SearchStringSchema.parse(val);
          });
          return { searchStr, ..._.pick(topic, LegacyTopicAttributes.Search) };
        },
      },
    }),
  ],
  derived: {
    id: topic => buildRecordId('legacyTopic', topic),
    topicGroupId: topic =>
      buildRecordId('legacyTopicGroup', { subject: topic.subject, code: topic.topicGroupCode }),
  },
};

export const encodeLegacyTopicToDb = createEncodeToDbFn(LegacyTopicCodecConfig);
export const decodeLegacyTopicFromDb = createDecodeFromDbFn(LegacyTopicCodecConfig);
